import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * <AreaLight3D>: a three.js RectAreaLight sized from `area_size`, which the patched chunks shade
 * with Godot's area light (`r3f/areaLight/`). It carries its range, attenuation, specular and
 * `area_texture` to the shader and its fog energy to the volumetric fog, and casts its shadow
 * through the positional shadow atlas (`AtlasAreaShadow`). It shows once Godot's LTC tables load.
 */
import { useMemo, useRef } from 'react';
import { visualLayersUserData } from '../../../../r3f/visualLayers';
import { node3dGroupSpread, transformFromNode3DProperties } from '../../../../r3f/nodeTransform';
import { parseColorToHex } from '../../../../utils/colorParser';
import { lightFogEnergy, lightIntensity, lightShadowOpacity } from '../shared/lightStrength';
import { useLightDistanceFade } from '../shared/lightDistanceFade';
import { lightBakeModeUserData } from '../../../../r3f/lightBakeMode/lightBakeMode';
import { areaLightUserData } from '../../../../r3f/areaLight/areaLightFalloff';
import { fogLightUserData } from '../../../../r3f/fog/fogLightDeclaration';
import { positionalShadowUserData } from '../../../../r3f/positionalShadow/declaration';
import { AtlasAreaShadow } from '../../../../r3f/positionalShadow/atlasAreaShadow';
import { AREA_LIGHT_3D_DEFAULTS } from '../../../../godot/scene/light';
import { positionalShadowDeclaration } from '../shared/positionalShadowDeclaration';
import { useHeldTexture } from '../../../../r3f/textureAtlas/useHeldTexture';
import { areaTextureUserData, holdAreaTexture, releaseAreaTexture, } from '../../../../r3f/areaLight/areaLightAtlas';
import { useLtcTexturesSettled } from '../../../../r3f/areaLight/useLtcTexturesSettled';
/**
 * Godot's colour scale: `energy * PI`, negated for a negative light, divided by the rectangle's area
 * when the light normalises its energy (4.7.2: `light_storage.cpp:1003`, `:1046`, `:1050`,
 * `:1094-1100`). Each render scales it by its exposure (`sceneExposure.ts`).
 */
function areaLightIntensity(properties) {
    const { x: width, y: height } = properties.area_size;
    const area = width * height;
    const intensity = lightIntensity(properties);
    return properties.area_normalize_energy && area > 0 ? intensity / area : intensity;
}
/**
 * The `area_texture`, held in the area light atlas while the light shows it. The atlas copies its
 * sRGB view (4.7.2 `texture_storage.cpp:3524`).
 */
const useAreaTexture = (ref) => useHeldTexture(ref, 'decoding', holdAreaTexture, releaseAreaTexture);
/**
 * The falloff and texture the shader reads, the shadow the fitter reads and the energy the fog
 * reads, rebuilt only when one of them changes.
 */
function useAreaLightUserData(properties) {
    const { layers, light_bake_mode, area_range, area_attenuation, light_specular, light_size, shadow_blur, shadow_normal_bias, } = properties;
    const texture = useAreaTexture(properties.area_texture);
    const fogEnergy = lightFogEnergy(properties);
    return useMemo(() => ({
        ...areaLightUserData({ range: area_range, attenuation: area_attenuation, specular: light_specular }),
        ...areaTextureUserData(texture),
        ...positionalShadowUserData(positionalShadowDeclaration({ light_size, shadow_blur, shadow_normal_bias })),
        ...fogLightUserData({ energy: fogEnergy }),
        ...lightBakeModeUserData(light_bake_mode),
        ...visualLayersUserData(layers),
    }), [
        layers,
        light_bake_mode,
        area_range,
        area_attenuation,
        light_specular,
        texture,
        light_size,
        shadow_blur,
        shadow_normal_bias,
        fogEnergy,
    ]);
}
export function AreaLight3D({ node, children }) {
    const properties = node.properties;
    const lightRef = useRef(null);
    const shadow = useMemo(() => new AtlasAreaShadow(), []);
    const transform = useMemo(() => transformFromNode3DProperties(properties), [properties]);
    const intensity = areaLightIntensity(properties);
    const shadowOpacity = lightShadowOpacity(properties);
    useLightDistanceFade(lightRef, properties, { intensity, shadowOpacity });
    const userData = useAreaLightUserData(properties);
    const isLtcLoadSettled = useLtcTexturesSettled();
    return (_jsxs("group", { name: node.name, ...node3dGroupSpread(transform), children: [_jsx("rectAreaLight", { ref: lightRef, visible: isLtcLoadSettled, color: parseColorToHex(properties.light_color), intensity: intensity, width: properties.area_size.x, height: properties.area_size.y, castShadow: properties.shadow_enabled, userData: userData, children: _jsx("primitive", { object: shadow, attach: "shadow", intensity: shadowOpacity, bias: properties.shadow_bias ?? AREA_LIGHT_3D_DEFAULTS.shadowBias, range: properties.area_range, size: [properties.area_size.x, properties.area_size.y] }) }), children] }));
}
//# sourceMappingURL=Component.js.map